import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ActiveFilter, FilterGroup } from 'shared/types/filter';
import {
  DocumentOrganizationKeys,
  FilterDocument,
  FilterOptionKeys,
} from 'shared/types/filter-document';
import { Goal } from 'shared/types/goal';
import { LifeArea } from 'shared/types/life-area';
import { GroupByMode, SortingMode } from 'shared/types/sorting-mode';
import { Tag } from 'shared/types/tag';
import { TaskPriorityOptions, TaskType } from 'shared/types/task-base';
import { createFilters } from 'shared/utils/filters';
import { createGroupingAndSorting } from 'shared/utils/filters/create-grouping-and-sorting';

export type Options = {
  goals: Goal[];
  lifeAreas: LifeArea[];
  tags: Tag[];
};

export const useFilterState = (
  filterDocument: FilterDocument,
  { goals, lifeAreas, tags }: Options,
) => {
  const { t } = useTranslation();
  const filters = useMemo<FilterGroup[]>(
    () => [
      ...createGroupingAndSorting({ t }),
      ...createFilters({ t, lifeAreas, tags, goals }),
    ],
    [goals, lifeAreas, t, tags],
  );

  const activeOptions = useMemo<ActiveFilter[]>(
    () => [
      {
        id: DocumentOrganizationKeys.SortingMode,
        value: [filterDocument.sortingMode ?? SortingMode.DueDate],
      },
      {
        id: DocumentOrganizationKeys.GroupByMode,
        value: [filterDocument.groupByMode ?? GroupByMode.None],
      },
      ...Object.keys(filterDocument.filters ?? {}).reduce<ActiveFilter[]>(
        (acc, key) => {
          const val =
            filterDocument.filters?.[
              key as keyof typeof filterDocument.filters
            ];

          if (key === FilterOptionKeys.Duration) {
            const durationFilter = val as {
              more?: { type: 'minutes'; value: number } | null;
              less?: { type: 'minutes'; value: number } | null;
            };
            const hasMore =
              durationFilter.more?.value !== undefined &&
              durationFilter.more?.value !== null;
            const hasLess =
              durationFilter.less?.value !== undefined &&
              durationFilter.less?.value !== null;

            if (hasMore || hasLess) {
              acc.push({
                id: key,
                value: [
                  hasMore ? durationFilter.more!.value.toString() : '',
                  hasLess ? durationFilter.less!.value.toString() : '',
                ],
              });
            }
            return acc;
          }

          if (!val || !('include' in val) || !val.include?.length) {
            return acc;
          }

          if (key === FilterOptionKeys.LifeArea) {
            const ids = val.include.filter((id) =>
              lifeAreas.find((lifeArea) => lifeArea.id === id),
            );
            acc.push({
              id: key,
              value: ids,
            });
            return acc;
          }

          if (key === FilterOptionKeys.Goal) {
            const ids = Array.from(
              new Set(
                val.include.filter((id) =>
                  goals.find((goal) => goal.id === id),
                ),
              ),
            );
            acc.push({
              id: key,
              value: ids,
            });
            return acc;
          }

          if (key === FilterOptionKeys.Priority) {
            acc.push({
              id: key,
              value: val.include as TaskPriorityOptions[],
            });
            return acc;
          }

          if (key === FilterOptionKeys.Type) {
            acc.push({
              id: key,
              value: val.include as TaskType[],
            });
            return acc;
          }

          if (key === FilterOptionKeys.Tag) {
            const ids = val.include.filter((id) =>
              tags.find((tag) => tag.id === id),
            );
            acc.push({
              id: key,
              value: ids,
            });
            return acc;
          }

          return acc;
        },
        [],
      ),
    ],
    [filterDocument, goals, lifeAreas, tags],
  );

  const activeFilters = useMemo(
    () =>
      activeOptions.reduce(
        (acc, { id, value }) =>
          acc +
          ([
            DocumentOrganizationKeys.SortingMode,
            DocumentOrganizationKeys.GroupByMode,
          ].includes(id as DocumentOrganizationKeys)
            ? 0
            : Array.isArray(value)
              ? value.length
              : 1),
        0,
      ),
    [activeOptions],
  );

  return {
    filters,
    activeFilters,
    activeOptions,
  };
};
